import React, { useState, useEffect } from 'react';
import { useAdminAppearance, COLUMN_WIDTH_DEFAULT, COLUMN_WIDTH_MIN, COLUMN_WIDTH_MAX } from '../../contexts/AdminAppearanceContext';
import { useLanguage } from '../../contexts/LanguageContext';
import api from '../../services/api';
import '../../styles/global.css';

function clampColumnWidth(value: number): number {
  const n = Number(value);
  if (Number.isNaN(n)) return COLUMN_WIDTH_DEFAULT;
  return Math.round(Math.max(COLUMN_WIDTH_MIN, Math.min(COLUMN_WIDTH_MAX, n)));
}

const AdminAppearance: React.FC = () => {
  const { setBothColumnWidths } = useAdminAppearance();
  const { t } = useLanguage();

  // Local state for editing
  const [leftWidth, setLeftWidth] = useState(COLUMN_WIDTH_DEFAULT);
  const [rightWidth, setRightWidth] = useState(COLUMN_WIDTH_DEFAULT);
  const [initialLeft, setInitialLeft] = useState(COLUMN_WIDTH_DEFAULT);
  const [initialRight, setInitialRight] = useState(COLUMN_WIDTH_DEFAULT);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [eanSyncing, setEanSyncing] = useState(false);
  const [eanSyncResult, setEanSyncResult] = useState<string | null>(null);
  const [printerMode, setPrinterMode] = useState<'browser' | 'zebra'>('browser');
  const [printerIp, setPrinterIp] = useState('');
  const [printerInitialMode, setPrinterInitialMode] = useState<'browser' | 'zebra'>('browser');
  const [printerInitialIp, setPrinterInitialIp] = useState('');
  const [printerSaving, setPrinterSaving] = useState(false);
  const [printerSaveResult, setPrinterSaveResult] = useState<string | null>(null);
  const [docsDownloading, setDocsDownloading] = useState(false);
  const [docsError, setDocsError] = useState(false);

  const downloadDocs = async () => {
    setDocsDownloading(true);
    setDocsError(false);
    try {
      const res = await api.get('/admin/technical-documentation', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Dokumentacja-techniczna-Spectrum-Management.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch {
      setDocsError(true);
    }
    setDocsDownloading(false);
  };

  // Pobierz ustawienia drukarki
  useEffect(() => {
    api.get('/admin/printer-settings')
      .then((res) => {
        const m = res.data.printer_mode === 'zebra' ? 'zebra' : 'browser';
        setPrinterMode(m);
        setPrinterInitialMode(m);
        setPrinterIp(res.data.printer_ip || '');
        setPrinterInitialIp(res.data.printer_ip || '');
      })
      .catch(() => {});
  }, []);

  // Pobierz obecne wartości admina przy montowaniu komponentu
  useEffect(() => {
    console.log('[AdminAppearance Component] Pobieranie wartości admina z /admin/appearance...');
    api.get<{ column_width_left: number; column_width_right: number }>('/admin/appearance')
      .then((res) => {
        console.log('[AdminAppearance Component] Odpowiedź:', res.data);
        const left = clampColumnWidth(res.data.column_width_left);
        const right = clampColumnWidth(res.data.column_width_right);
        setLeftWidth(left);
        setRightWidth(right);
        setInitialLeft(left);
        setInitialRight(right);
        setIsLoading(false);
      })
      .catch((err) => {
        console.error('[AdminAppearance Component] Błąd:', err);
        setIsLoading(false);
      });
  }, []);

  // Check if there are unsaved changes
  const hasChanges = leftWidth !== initialLeft || rightWidth !== initialRight;

  const handleChangeLeft = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (raw === '') {
      setLeftWidth(COLUMN_WIDTH_MIN);
      return;
    }
    const parsed = parseInt(raw, 10);
    if (Number.isNaN(parsed)) return;
    const clamped = Math.round(Math.max(COLUMN_WIDTH_MIN, Math.min(COLUMN_WIDTH_MAX, parsed)));
    setLeftWidth(clamped);
  };

  const handleChangeRight = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (raw === '') {
      setRightWidth(COLUMN_WIDTH_MIN);
      return;
    }
    const parsed = parseInt(raw, 10);
    if (Number.isNaN(parsed)) return;
    const clamped = Math.round(Math.max(COLUMN_WIDTH_MIN, Math.min(COLUMN_WIDTH_MAX, parsed)));
    setRightWidth(clamped);
  };

  const handleResetLeft = () => {
    setLeftWidth(COLUMN_WIDTH_DEFAULT);
  };

  const handleResetRight = () => {
    setRightWidth(COLUMN_WIDTH_DEFAULT);
  };

  const handleResetBoth = () => {
    setLeftWidth(COLUMN_WIDTH_DEFAULT);
    setRightWidth(COLUMN_WIDTH_DEFAULT);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveSuccess(false);

    console.log('[AdminAppearance Component] Zapisuję na serwer:', { left: leftWidth, right: rightWidth });

    try {
      // Zapisz bezpośrednio na serwer (PUT /admin/appearance)
      const response = await api.put('/admin/appearance', {
        column_width_left: leftWidth,
        column_width_right: rightWidth,
      });

      console.log('[AdminAppearance Component] Zapisano na serwerze:', response.data);

      // Zaktualizuj context OBE wartości naraz (bez wysyłania na serwer ponownie)
      setBothColumnWidths(leftWidth, rightWidth);

      // Update initial values to reflect saved state
      setInitialLeft(leftWidth);
      setInitialRight(rightWidth);

      setIsSaving(false);
      setSaveSuccess(true);

      // Hide success message after 2 seconds
      setTimeout(() => {
        setSaveSuccess(false);
      }, 2000);
    } catch (err) {
      console.error('[AdminAppearance Component] Błąd zapisu:', err);
      setIsSaving(false);
      alert('Błąd zapisu ustawień!');
    }
  };

  if (isLoading) {
    return (
      <div>
        <div className="page-header">
          <h1>{t('appearance.title')}</h1>
        </div>
        <div className="card" style={{ maxWidth: '500px', marginTop: '20px' }}>
          <p>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1>{t('appearance.title')}</h1>
      </div>

      <div className="card" style={{ maxWidth: '500px', marginTop: '20px' }}>
        <h2 style={{ marginTop: 0, marginBottom: '20px', color: '#2d3748', fontSize: '18px' }}>
          {t('appearance.tableColumnWidth')}
        </h2>

        {hasChanges && (
          <div style={{
            padding: '10px',
            backgroundColor: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '8px',
            marginBottom: '20px',
            fontSize: '14px',
            color: '#856404'
          }}>
            ⚠️ {t('common.warning')}: {t('appearance.unsavedChanges') || 'Masz niezapisane zmiany'}
          </div>
        )}

        {saveSuccess && (
          <div style={{
            padding: '10px',
            backgroundColor: '#d4edda',
            border: '1px solid #28a745',
            borderRadius: '8px',
            marginBottom: '20px',
            fontSize: '14px',
            color: '#155724'
          }}>
            ✓ {t('appearance.savedSuccessfully') || 'Zmiany zostały zapisane!'}
          </div>
        )}

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#4a5568' }}>
            {t('appearance.leftColumnWidth')}
          </label>
          <input
            type="number"
            min={COLUMN_WIDTH_MIN}
            max={COLUMN_WIDTH_MAX}
            value={leftWidth}
            onChange={handleChangeLeft}
            className="input"
            style={{ width: '120px' }}
          />
          <span style={{ marginLeft: '8px', color: '#718096', fontSize: '14px' }}>
            ({COLUMN_WIDTH_MIN}–{COLUMN_WIDTH_MAX})
          </span>
          <button type="button" onClick={handleResetLeft} className="btn btn-outline" style={{ fontSize: '14px', marginLeft: '12px' }}>
            {t('appearance.restoreDefault')} ({COLUMN_WIDTH_DEFAULT})
          </button>
        </div>
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#4a5568' }}>
            {t('appearance.rightColumnWidth')}
          </label>
          <input
            type="number"
            min={COLUMN_WIDTH_MIN}
            max={COLUMN_WIDTH_MAX}
            value={rightWidth}
            onChange={handleChangeRight}
            className="input"
            style={{ width: '120px' }}
          />
          <span style={{ marginLeft: '8px', color: '#718096', fontSize: '14px' }}>
            ({COLUMN_WIDTH_MIN}–{COLUMN_WIDTH_MAX})
          </span>
          <button type="button" onClick={handleResetRight} className="btn btn-outline" style={{ fontSize: '14px', marginLeft: '12px' }}>
            {t('appearance.restoreDefault')} ({COLUMN_WIDTH_DEFAULT})
          </button>
        </div>

        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button
            type="button"
            onClick={handleSave}
            className="btn btn-primary"
            disabled={!hasChanges || isSaving}
            style={{
              fontSize: '14px',
              opacity: (!hasChanges || isSaving) ? 0.6 : 1,
              cursor: (!hasChanges || isSaving) ? 'not-allowed' : 'pointer'
            }}
          >
            {isSaving ? (t('common.loading') || 'Zapisywanie...') : (t('common.save') || 'Zapisz')}
          </button>

          <button
            type="button"
            onClick={handleResetBoth}
            className="btn btn-outline"
            style={{ fontSize: '14px' }}
          >
            {t('appearance.restoreBothDefaults')} ({COLUMN_WIDTH_DEFAULT})
          </button>
        </div>
      </div>
      <div className="card" style={{ maxWidth: '500px', marginTop: '20px' }}>
        <h2 style={{ marginTop: 0, marginBottom: '20px', color: '#2d3748', fontSize: '18px' }}>
          Ustawienia drukarki etykiet
        </h2>

        {printerSaveResult && (
          <div style={{
            padding: '10px',
            backgroundColor: '#d4edda',
            border: '1px solid #28a745',
            borderRadius: '8px',
            marginBottom: '15px',
            fontSize: '14px',
            color: '#155724'
          }}>
            {printerSaveResult}
          </div>
        )}

        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#4a5568' }}>
            Tryb drukowania:
          </label>
          <select
            value={printerMode}
            onChange={(e) => setPrinterMode(e.target.value as 'browser' | 'zebra')}
            className="input"
            style={{ width: '250px' }}
          >
            <option value="browser">Drukarka systemowa (okno druku)</option>
            <option value="zebra">Zebra ZPL (sieciowo)</option>
          </select>
        </div>

        {printerMode === 'zebra' && (
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#4a5568' }}>
              Adres IP drukarki Zebra:
            </label>
            <input
              type="text"
              value={printerIp}
              onChange={(e) => setPrinterIp(e.target.value)}
              placeholder="np. 192.168.1.100"
              className="input"
              style={{ width: '250px' }}
            />
            <small style={{ display: 'block', marginTop: '4px', color: '#718096', fontSize: '12px' }}>
              Drukarka musi byc w tej samej sieci, port 9100
            </small>
          </div>
        )}

        <button
          type="button"
          onClick={async () => {
            setPrinterSaving(true);
            setPrinterSaveResult(null);
            try {
              await api.put('/admin/printer-settings', { printer_mode: printerMode, printer_ip: printerIp });
              setPrinterInitialMode(printerMode);
              setPrinterInitialIp(printerIp);
              setPrinterSaveResult('Ustawienia drukarki zapisane');
              setTimeout(() => setPrinterSaveResult(null), 3000);
            } catch (err) {
              setPrinterSaveResult('Blad zapisu ustawien drukarki');
            }
            setPrinterSaving(false);
          }}
          className="btn btn-primary"
          disabled={printerSaving || (printerMode === printerInitialMode && printerIp === printerInitialIp)}
          style={{
            fontSize: '14px',
            opacity: (printerSaving || (printerMode === printerInitialMode && printerIp === printerInitialIp)) ? 0.6 : 1,
            cursor: (printerSaving || (printerMode === printerInitialMode && printerIp === printerInitialIp)) ? 'not-allowed' : 'pointer'
          }}
        >
          {printerSaving ? 'Zapisywanie...' : 'Zapisz ustawienia drukarki'}
        </button>
      </div>

      <div className="card" style={{ maxWidth: '500px', marginTop: '20px' }}>
        <h2 style={{ marginTop: 0, marginBottom: '20px', color: '#2d3748', fontSize: '18px' }}>
          Synchronizacja EAN
        </h2>
        <p style={{ fontSize: '14px', color: '#4a5568', marginBottom: '15px' }}>
          Zsynchronizuj kody EAN z zaimportowanych plik&oacute;w Excel do stacji pakowania.
          Dotyczy produkt&oacute;w, kt&oacute;re maj&#261; EAN w pliku ale nie s&#261; jeszcze widoczne na stacji pakowania.
        </p>

        {eanSyncResult && (
          <div style={{
            padding: '10px',
            backgroundColor: '#d4edda',
            border: '1px solid #28a745',
            borderRadius: '8px',
            marginBottom: '15px',
            fontSize: '14px',
            color: '#155724'
          }}>
            {eanSyncResult}
          </div>
        )}

        <button
          type="button"
          onClick={async () => {
            setEanSyncing(true);
            setEanSyncResult(null);
            try {
              const res = await api.post('/eans/sync');
              setEanSyncResult(res.data.message);
            } catch (err) {
              setEanSyncResult('Blad synchronizacji EAN');
            }
            setEanSyncing(false);
          }}
          className="btn btn-primary"
          disabled={eanSyncing}
          style={{
            fontSize: '14px',
            opacity: eanSyncing ? 0.6 : 1,
            cursor: eanSyncing ? 'not-allowed' : 'pointer'
          }}
        >
          {eanSyncing ? 'Synchronizowanie...' : 'Synchronizuj EAN-y'}
        </button>
      </div>

      <div className="card" style={{ maxWidth: '500px', marginTop: '20px' }}>
        <h2 style={{ marginTop: 0, marginBottom: '20px', color: '#2d3748', fontSize: '18px' }}>
          {t('appearance.docsTitle')}
        </h2>
        <p style={{ fontSize: '14px', color: '#4a5568', marginBottom: '15px' }}>
          {t('appearance.docsDescription')}
        </p>

        {docsError && (
          <div style={{
            padding: '10px',
            backgroundColor: '#fde8e8',
            border: '1px solid #dc2626',
            borderRadius: '8px',
            marginBottom: '15px',
            fontSize: '14px',
            color: '#991b1b'
          }}>
            {t('appearance.docsError')}
          </div>
        )}

        <button
          type="button"
          onClick={downloadDocs}
          className="btn btn-primary"
          disabled={docsDownloading}
          style={{
            fontSize: '14px',
            opacity: docsDownloading ? 0.6 : 1,
            cursor: docsDownloading ? 'not-allowed' : 'pointer'
          }}
        >
          {docsDownloading ? t('appearance.docsDownloading') : t('appearance.docsDownload')}
        </button>
      </div>
    </div>
  );
};

export default AdminAppearance;
